import React, { CSSProperties, useCallback, useEffect, useRef, useState } from "react";

import { Button, Form, Input } from "semantic-ui-react";

import EmailLoginManager from "react-lib/apps/IsHealth/Common/EmailLogin/EmailLoginManager";

import "./EmailVerificationForm.scss";

type EmailVerificationFormProps = {
  email: string;
  onSuccess: (res: any) => void;
};

type Styles = Record<"errorMessage" | "submitButton", CSSProperties>;

const styles: Styles = {
  errorMessage: {
    alignItems: "center",
    backgroundColor: "#fff1f0",
    border: "1px solid #ffccc7",
    borderRadius: "8px",
    color: "#cf1322",
    display: "flex",
    fontSize: "13px",
    marginTop: "8px",
    padding: "12px 16px",
  },
  submitButton: {
    backgroundColor: "var(--primary-theme-color)",
    borderRadius: "28px",
    fontSize: "15px",
    width: "68%",
  },
};

const OTP_LENGTH = 6;
const COUNTDOWN_DURATION = 60;

const EmailVerificationForm: React.FC<EmailVerificationFormProps> = (props) => {
  const manager = useRef(new EmailLoginManager());

  const [otpCode, setOtpCode] = useState<string[]>(Array.from({ length: OTP_LENGTH }, () => ""));
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [canResend, setCanResend] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_DURATION);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, OTP_LENGTH);
    // Auto-focus first input when component mounts
    setTimeout(() => inputRefs.current[0]?.focus(), 100);
  }, []);

  // Countdown timer for resend button
  useEffect(() => {
    let timer: any;

    if (countdown > 0 && !canResend) {
      timer = setTimeout(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else if (countdown === 0) {
      setCanResend(true);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [canResend, countdown]);

  const clearError = useCallback(() => {
    if (errorMessage) {
      setErrorMessage("");
    }
  }, [errorMessage]);

  const clearOtpAndFocus = useCallback(() => {
    setOtpCode(Array.from({ length: OTP_LENGTH }, () => ""));
    setTimeout(() => inputRefs.current[0]?.focus(), 50);
  }, []);

  const handleOtpChange = (index: number, value: string) => {
    // Allow only numeric values
    if (!/^\d*$/.test(value) || value.length > 1) {
      return;
    }

    const updateOtpCode = [...otpCode];

    updateOtpCode[index] = value;
    setOtpCode(updateOtpCode);

    // Auto focus next input
    if (value && index < OTP_LENGTH - 1) {
      const nextInput = inputRefs.current[index + 1];

      if (nextInput) {
        nextInput.focus();
        nextInput.select();
      }
    }

    clearError();
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace") {
      if (!otpCode[index] && index > 0) {
        // If current field is empty, go to previous field
        const prevInput = inputRefs.current[index - 1];

        if (prevInput) {
          prevInput.focus();
        }
      } else {
        // Clear current field
        const updateOtpCode = [...otpCode];

        updateOtpCode[index] = "";
        setOtpCode(updateOtpCode);
        clearError();
      }
    }

    if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text");

    // Extract only numeric characters and limit to OTP_LENGTH
    const numericData = pasteData.replaceAll(/\D/g, "").slice(0, OTP_LENGTH);

    if (numericData.length === 0) {
      return;
    }

    clearError();

    const updateOtpCode = Array.from(
      { length: OTP_LENGTH },
      (unused, index) => numericData[index] || ""
    );

    setOtpCode(updateOtpCode);

    // Focus on the appropriate input
    const nextIndex = Math.min(numericData.length, OTP_LENGTH - 1);
    const nextInput = inputRefs.current[nextIndex];

    if (nextInput) {
      setTimeout(() => nextInput.focus(), 50);
    }

    // Auto-submit if all fields are filled
    if (numericData.length === OTP_LENGTH) {
      setTimeout(async () => handleVerifyEmail(updateOtpCode), 500);
    }
  };

  const handleVerifyEmail = async (codeArray?: string[]) => {
    const currentOtp = codeArray ?? otpCode;
    const code = currentOtp.join("");

    if (code.length !== OTP_LENGTH) {
      setErrorMessage(`กรุณากรอกรหัส OTP ให้ครบ ${OTP_LENGTH} หลัก`);

      return;
    }

    setErrorMessage("");
    setIsLoading(true);

    try {
      const res = await manager.current.verifyEmail(props.email, code);

      setIsLoading(false);
      props.onSuccess(res);
    } catch {
      setErrorMessage("รหัส OTP ไม่ถูกต้อง กรุณาลองใหม่อีกครั้ง");
      setIsLoading(false);

      // Clear OTP on error and focus first input
      setTimeout(() => clearOtpAndFocus(), 100);
    }
  };

  const handleResendCode = async () => {
    if (!canResend) {
      return;
    }

    try {
      setCanResend(false);
      setCountdown(COUNTDOWN_DURATION);
      setErrorMessage("");
      clearOtpAndFocus();

      await manager.current.requestLogin(props.email);
    } catch (error: any) {
      console.error("Failed to resend code:", error);

      setErrorMessage("ไม่สามารถส่งรหัสใหม่ได้ กรุณาลองใหม่อีกครั้ง");
      setCanResend(true);
      setCountdown(0);
    }
  };

  const handleInputClick = (index: number) => {
    // Select all text when clicking on input
    inputRefs.current[index]?.select();
  };

  return (
    <>
      <div className="email-verification__container">
        <p className="email-verification__description">กรุณาใส่รหัส OTP จาก Email ที่ส่งไปยัง</p>

        <div className="email-verification__email-display">
          <span className="email-verification__email-display-icon">✉</span>
          <span style={{ margin: "auto", marginTop: "-1px" }}>{props.email}</span>
        </div>
      </div>

      <Form style={{ marginBottom: "1.4rem" }}>
        <div className="email-verification__otp-container">
          {otpCode.map((digit, index) => (
            <Input
              key={index}
              ref={(el: any) => {
                inputRefs.current[index] = el?.inputRef?.current || null;
              }}
              className="email-verification__otp-input"
              autoComplete="one-time-code"
              disabled={isLoading}
              inputMode="numeric"
              maxLength={1}
              pattern="\d*"
              size="large"
              type="text"
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onClick={() => {
                handleInputClick(index);
              }}
              onKeyDown={(e: any) => {
                handleKeyDown(index, e);
              }}
              onPaste={handlePaste}
            />
          ))}
        </div>

        {!!errorMessage && (
          <div style={styles.errorMessage}>
            <span style={{ marginRight: "8px" }}>⚠</span>
            <span>{errorMessage}</span>
          </div>
        )}
      </Form>

      <div className="email-verification__resend-container">
        <button
          className="email-verification__resend-button"
          disabled={!canResend || isLoading}
          onClick={handleResendCode}
        >
          {canResend ? "ขอรหัสใหม่อีกครั้ง" : `ขอรหัสใหม่อีกครั้งใน ${countdown} วินาที`}
        </button>
      </div>

      <div style={{ display: "flex", justifyContent: "center" }}>
        <Button
          style={styles.submitButton}
          disabled={isLoading}
          loading={isLoading}
          size="large"
          onClick={async () => handleVerifyEmail()}
        >
          ดำเนินการต่อ
        </Button>
      </div>
    </>
  );
};

export default EmailVerificationForm;
